import { HttpContext } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LoggingService } from '@common/logging/logging.service';
import { filter, firstValueFrom } from 'rxjs';
import type { User } from 'src/proxy';
import { UsersService as UsersServiceProxy } from 'src/proxy';

import { SKIP_FORBIDDEN_INTERCEPTOR } from '@/core/interceptors/http-context-tokens';

import { WebSocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  readonly #usersServiceProxy = inject(UsersServiceProxy);
  readonly #webSocketService = inject(WebSocketService);
  readonly #logger = inject(LoggingService);
  readonly #destroyRef = inject(DestroyRef);

  readonly #userData = signal<User | null | undefined>(undefined);
  readonly userData = this.#userData.asReadonly();

  readonly #userDataLoaded$ = toObservable(this.#userData).pipe(
    filter((u): u is User | null => u !== undefined)
  );

  #hasFetched = false;

  constructor() {
    this.ensureLoaded();

    this.#webSocketService
      .on('user.permissions_changed')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.#logger.debug('Permissions changed, refreshing user data');
        this.refresh();
      });
  }

  /**
   * Trigger initial fetch if not yet loaded, or force a refresh.
   * Called automatically when `userData` is first read via `ensureLoaded()`,
   * or explicitly to re-fetch after permission changes.
   */
  refresh(): void {
    this.#hasFetched = true;
    const context = new HttpContext().set(SKIP_FORBIDDEN_INTERCEPTOR, true);
    this.#usersServiceProxy
      .usersUsersMeRetrieve('body', false, { context })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: error => {
          this.#logger.error('Error fetching user data', error as Error);
          this.#userData.set(null);
        },
        next: user => {
          return this.#userData.set(user);
        },
      });
  }

  /**
   * Ensure user data has been loaded at least once.
   * Safe to call multiple times -- only fetches on the first call.
   */
  ensureLoaded(): void {
    if (!this.#hasFetched) {
      this.refresh();
    }
  }

  /**
   * Returns a promise that resolves once user data has been fetched
   * (either successfully or with an error).  Safe for route guards
   * that need to wait for the async user load before checking roles
   * or permissions.
   */
  whenLoaded(): Promise<User | null> {
    this.ensureLoaded();
    return firstValueFrom(this.#userDataLoaded$);
  }
}
